import { useCallback } from 'react'
import { Experience } from '@ninetailed/experience.js-react'

import { mapNtExperiences } from '@/utils/ninetailed'
import Image from '@/components/ui/image'
import RichText from '@/components/ui/rich-text'
import { cx, slugify } from '@/utils/strings'
import { type TypeSectionSetOfImagesSkeleton } from '@/types/ctf/TypeSectionSetOfImages'
import type { Entry } from 'contentful'
import Marquee from '@/components/ui/marquee'
import ShieldLoader from '@/components/ui/shield-loader'

type SectionOfImagesEntry = Entry<TypeSectionSetOfImagesSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>

export type SetOfImageProps = SectionOfImagesEntry['fields'] & {
  className?: string
  pageOrderNo: number
  displayAsMarquee?: boolean
  reverseMarqueeDirection?: boolean
  eyebrowText?: string
  hideOnMobile?: boolean
}

/**
 * A set of images
 * - Requires a minimum of 2 images
 * - Has a maximum of 6 images
 */

export const SetOfImagesComponent = ({
  className,
  pageOrderNo,
  displayAsMarquee,
  reverseMarqueeDirection,
  heading,
  body,
  eyebrowText,
  images,
  imageSize = '128X128',
  internalName,
  hideOnMobile = false,
}: SetOfImageProps) => {

  const Container = useCallback(
    displayAsMarquee
      ? Marquee
      : ({ children }: { children: any }) => (
          <div className={cx('flex justify-center items-center flex-wrap gap-8')}>
            {children}
          </div>
        ),
    [displayAsMarquee, imageSize, images?.length]
  )

  return images && images.length > 1 ? (
    <section id={slugify(internalName)} className={cx(className, hideOnMobile && 'hidden md:block')}>
      <div className="mx-auto max-w-8xl px-5 lg:px-9">
        {(heading || body || eyebrowText) && (
          <div className={'text-center font-normal mb-6'}>
            {eyebrowText && <p className="text-eyebrow uppercase">{eyebrowText}</p>}
            {pageOrderNo === 1 ? <h2 className="h3">{heading}</h2> : <h3>{heading}</h3>}

            {body && (
              <div className="md:w-[60%] mx-auto">
                <RichText body={body} />
              </div>
            )}
          </div>
        )}
        <Container isReversed={reverseMarqueeDirection}>
          {images?.map((image) => (
            <div
              key={image?.sys?.id}
              className={cx(
                imageSize === '32X32' && `[&_img]:w-[32px] [&_svg]:w-[32px]`,
                imageSize === '64X64' && `[&_img]:w-[64px] [&_svg]:w-[64px]`,
                imageSize === '96X96' && `[&_img]:w-[96px] [&_svg]:w-[96px]`,
                imageSize === '128X128' && `[&_img]:w-[128px] [&_svg]:w-[128px]`,
                imageSize === '190X190' && `[&_img]:w-[190px] [&_svg]:w-[190px]`,
                imageSize === 'Full' && `[&_img]:w-[190px] [&_svg]:w-[190px]`,
                displayAsMarquee && 'flex justify-center items-center [&_figure]:mb-0 [&_img]:m-auto [&_svg]:m-auto',
              )}
            >
              <Image image={image?.fields.image} {...image?.fields} />
            </div>
          ))}
        </Container>
      </div>
    </section>
  ) : (
    <div />
  )
}

export default function SetOfImages(props: any): JSX.Element {
  const { nt_experiences, id, pageOrderNo } = props

  const ShieldBeatLoader = useCallback(() => <ShieldLoader containerHeight="450" />, [])

  return (
    <Experience
      {...props}
      id={id}
      experiences={mapNtExperiences(nt_experiences)}
      component={SetOfImagesComponent}
      loadingComponent={ShieldBeatLoader}
      passthroughProps={{ pageOrderNo }}
    />
  )
}
